import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useRealtimeStockPrices
 * Subscribes to stocks table UPDATE events via Supabase Realtime.
 * Provides: live status badge, optimistic price updates, last_updated timestamp,
 * polling fallback when realtime fails, and cleanup on unmount.
 */
export function useRealtimeStockPrices(onStockUpdate, options = {}) {
  const { enabled = true, pollIntervalMs = 30000 } = options;

  const [subscriptionStatus, setSubscriptionStatus] = useState('idle'); // idle | connecting | connected | error | closed
  const [isLive, setIsLive] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState('');

  const channelRef = useRef(null);
  const pollingRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);
  const MAX_RETRIES = 5;

  const formatLastUpdated = useCallback((ts) => {
    if (!ts) return '';
    const diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 5) return 'Updated just now';
    if (diff < 60) return `Updated ${diff}s ago`;
    if (diff < 3600) return `Updated ${Math.floor(diff / 60)}m ago`;
    return `Updated ${Math.floor(diff / 3600)}h ago`;
  }, []);

  const markUpdated = useCallback(() => {
    const now = Date.now();
    setLastUpdated(now);
    setLastUpdatedLabel('Updated just now');
  }, []);

  // Refresh label every 5 seconds
  useEffect(() => {
    if (!lastUpdated) return;
    const interval = setInterval(() => {
      if (mountedRef?.current) {
        setLastUpdatedLabel(formatLastUpdated(lastUpdated));
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [lastUpdated, formatLastUpdated]);

  const stopPolling = useCallback(() => {
    if (pollingRef?.current) {
      clearInterval(pollingRef?.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((fetchFn) => {
    stopPolling();
    if (fetchFn) {
      pollingRef.current = setInterval(() => {
        if (mountedRef?.current) {
          console.log('[StockRealtime] Polling fallback triggered');
          fetchFn();
        }
      }, pollIntervalMs);
    }
  }, [pollIntervalMs, stopPolling]);

  const cleanup = useCallback(() => {
    if (retryTimerRef?.current) {
      clearTimeout(retryTimerRef?.current);
      retryTimerRef.current = null;
    }
    if (channelRef?.current) {
      supabase?.removeChannel(channelRef?.current);
      channelRef.current = null;
    }
    stopPolling();
  }, [stopPolling]);

  const subscribe = useCallback((fallbackFetch) => {
    if (!mountedRef?.current || !enabled) return;
    cleanup();

    if (!mountedRef?.current) return;
    setSubscriptionStatus('connecting');
    setIsLive(false);
    console.log('[StockRealtime] Subscribing to stocks table...');

    const channel = supabase?.channel('stock-prices-live')?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'stocks' },
        (payload) => {
          if (!mountedRef?.current) return;
          console.log('[StockRealtime] Stock UPDATE received:', payload?.new?.ticker);
          markUpdated();
          if (onStockUpdate) onStockUpdate(payload);
        }
      )?.subscribe((status, err) => {
        if (!mountedRef?.current) return;
        console.log('[StockRealtime] Status:', status, err || '');

        if (status === 'SUBSCRIBED') {
          setSubscriptionStatus('connected');
          setIsLive(true);
          retryCountRef.current = 0;
          stopPolling();
        } else if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          setSubscriptionStatus('error');
          setIsLive(false);
          console.warn('[StockRealtime] Connection issue:', status, err);

          // Start polling fallback
          startPolling(fallbackFetch);

          if (retryCountRef?.current < MAX_RETRIES) {
            retryCountRef.current += 1;
            const delay = 3000 * Math.pow(1.5, retryCountRef?.current - 1);
            console.log(`[StockRealtime] Retry ${retryCountRef?.current}/${MAX_RETRIES} in ${Math.round(delay / 1000)}s`);
            retryTimerRef.current = setTimeout(() => {
              if (mountedRef?.current) subscribe(fallbackFetch);
            }, delay);
          } else {
            setSubscriptionStatus('closed');
            console.error('[StockRealtime] Max retries reached, staying on polling fallback');
          }
        }
      });

    channelRef.current = channel;
  }, [enabled, cleanup, onStockUpdate, markUpdated, startPolling, stopPolling]);

  const forceReconnect = useCallback((fallbackFetch) => {
    console.log('[StockRealtime] Manual reconnect triggered');
    retryCountRef.current = 0;
    subscribe(fallbackFetch);
  }, [subscribe]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  return {
    subscriptionStatus,
    isLive,
    lastUpdated,
    lastUpdatedLabel,
    subscribe,
    forceReconnect,
    markUpdated,
  };
}
